import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { channels, siteInfo } from "@/mocks/channels";
import { topics } from "@/mocks/topics";

const navLinks = [
  { to: "/", label: "首页" },
  { to: "/rank", label: "热议榜" },
  { to: "/about", label: "关于我们" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false); // 移动端菜单
  const [chOpen, setChOpen] = useState(false); // 桌面频道下拉
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setOpen(false);
    setChOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);

  const results = useMemo(() => {
    const kw = keyword.trim();
    if (!kw) return [];
    return topics
      .filter((t) => t.title.includes(kw) || t.tags.some((tag) => tag.includes(kw)))
      .slice(0, 6);
  }, [keyword]);

  function submitSearch() {
    const kw = keyword.trim();
    const first =
      topics.find((t) => t.title.includes(kw)) ||
      topics.find((t) => t.tags.some((tag) => tag.includes(kw)));
    setSearchOpen(false);
    setKeyword("");
    if (first) {
      navigate(`/topics/${first.slug}`);
    } else {
      navigate("/rank");
    }
  }

  return (
    <header className="sticky top-0 z-40">
      <nav className="border-b border-background-200/80 bg-background-50/80 backdrop-blur-md">
        <div className="wrap flex h-16 items-center justify-between gap-4">
          {/* 品牌 */}
          <Link
            to="/"
            className="group flex items-center gap-2 whitespace-nowrap"
            aria-label={siteInfo.brand}
          >
            <span className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-lg text-background-50 transition-transform group-hover:-rotate-6">
              <i className="ri-megaphone-line" aria-hidden="true" />
            </span>
            <span className="font-display text-xl leading-none text-foreground-900">
              {siteInfo.brand}
            </span>
          </Link>

          {/* 桌面导航 */}
          <div className="hidden items-center gap-1 md:flex">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`rounded-full px-4 py-2 text-sm transition-colors ${
                  location.pathname === l.to
                    ? "bg-primary-100 font-bold text-primary-700"
                    : "text-foreground-700 hover:bg-background-200/70 hover:text-foreground-900"
                }`}
              >
                {l.label}
              </Link>
            ))}

            {/* 频道下拉 */}
            <div
              className="relative"
              onMouseEnter={() => setChOpen(true)}
              onMouseLeave={() => setChOpen(false)}
            >
              <button
                type="button"
                className="flex items-center gap-1 whitespace-nowrap rounded-full px-4 py-2 text-sm text-foreground-700 hover:bg-background-200/70 hover:text-foreground-900"
                aria-haspopup="true"
                aria-expanded={chOpen}
                onClick={() => setChOpen((v) => !v)}
              >
                频道广场
                <i className={`ri-arrow-down-s-line transition-transform ${chOpen ? "rotate-180" : ""}`} />
              </button>
              <div
                className={`absolute right-0 top-full w-56 overflow-hidden rounded-xl border border-background-200 bg-background-50 p-1.5 shadow-sm transition-all duration-200 ${
                  chOpen ? "translate-y-1 opacity-100" : "pointer-events-none translate-y-0 opacity-0"
                }`}
              >
                {channels.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/channels/${c.slug}`}
                    className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100"
                  >
                    <span
                      className={`flex h-7 w-7 items-center justify-center rounded-md text-base ${
                        c.accent === "primary"
                          ? "bg-primary-100 text-primary-700"
                          : c.accent === "accent"
                            ? "bg-accent-100 text-accent-700"
                            : "bg-secondary-200/70 text-secondary-800"
                      }`}
                    >
                      <i className={c.icon} aria-hidden="true" />
                    </span>
                    <span className="flex flex-col">
                      <span className="font-semibold text-foreground-800">{c.name}</span>
                      <span className="text-xs text-foreground-500">{c.tagline}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* 右侧：搜索 + 移动端菜单按钮 */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full text-xl text-foreground-700 transition-colors hover:bg-background-200/70"
              aria-label="搜索话题"
            >
              <i className="ri-search-line" />
            </button>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center rounded-full text-xl text-foreground-800 md:hidden"
              aria-label="打开菜单"
              aria-expanded={open}
            >
              <i className={open ? "ri-close-line" : "ri-menu-line"} />
            </button>
          </div>
        </div>

        {/* 移动端菜单 */}
        <div
          className={`overflow-hidden border-t border-background-200/60 transition-all duration-300 md:hidden ${
            open ? "max-h-[520px]" : "max-h-0 border-t-0"
          }`}
        >
          <div className="wrap flex flex-col gap-1 py-3">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                {l.label}
              </Link>
            ))}
            <p className="px-3 pt-2 text-xs text-foreground-400">频道广场</p>
            <div className="grid grid-cols-2 gap-1.5">
              {channels.map((c) => (
                <Link
                  key={c.slug}
                  to={`/channels/${c.slug}`}
                  className="flex items-center gap-2 rounded-lg bg-background-100 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-200"
                >
                  <i className={`${c.icon} text-base`} aria-hidden="true" />
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* 搜索弹层 */}
      {searchOpen && (
        <div
          className="fixed inset-0 z-50 bg-foreground-950/40 backdrop-blur-sm"
          onClick={() => setSearchOpen(false)}
        >
          <div
            className="wrap pt-20"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto max-w-2xl overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-lg">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  submitSearch();
                }}
                className="flex items-center gap-2 border-b border-background-200 px-4 py-3"
              >
                <i className="ri-search-line text-xl text-foreground-400" />
                <input
                  ref={inputRef}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索你想吃瓜的热门话题…"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                <button
                  type="submit"
                  className="whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  搜索
                </button>
              </form>
              <div className="max-h-80 overflow-y-auto p-2 scrollbar-thin">
                {keyword.trim() === "" && (
                  <div className="flex flex-wrap gap-2 p-2">
                    <p className="w-full pb-1 text-xs text-foreground-400">大家都在搜：</p>
                    {topics.slice(0, 5).map((t) => (
                      <button
                        key={t.slug}
                        type="button"
                        onClick={() => {
                          setKeyword(t.tags[0]);
                        }}
                        className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-600 hover:bg-primary-100 hover:text-primary-700"
                      >
                        {t.tags[0]}
                      </button>
                    ))}
                  </div>
                )}
                {results.length === 0 && keyword.trim() !== "" && (
                  <p className="px-3 py-6 text-center text-sm text-foreground-500">
                    没有找到相关话题，去热议榜看看大家都在聊什么吧～
                  </p>
                )}
                {results.map((t) => {
                  const ch = channels.find((c) => c.slug === t.channel);
                  return (
                    <button
                      key={t.slug}
                      type="button"
                      onClick={() => {
                        setSearchOpen(false);
                        setKeyword("");
                        navigate(`/topics/${t.slug}`);
                      }}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-background-100"
                    >
                      <span className="text-lg text-foreground-300">
                        <i className="ri-chat-3-line" />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-semibold text-foreground-800">
                          {t.title}
                        </span>
                        <span className="truncate text-xs text-foreground-500">{t.summary}</span>
                      </span>
                      <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800">
                        {ch?.label ?? "话题"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}